import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type {
  ShelterAdoption,
  ShelterFundraiser,
  ShelterSectionsBundle,
  ShelterSectionsMap,
  ShelterUrgentNeed,
  AdoptionSpecies,
} from "@/types/shelterSections";
import { isSectionUrgentActive } from "@/types/shelterSections";

// Batch fetch: dla listy aktualnie widocznych schronisk pobieramy 3 tabele rownolegle.
// Wynik to mapa shelterId -> { urgentNeeds, fundraiser (1), adoption (1, najpierw urgent) }.
// Klucz queryKey to posortowana lista ID, dzieki czemu react-query cache'uje
// zestawy widocznych schronisk niezaleznie od kolejnosci.

export const useShelterSections = (shelterIds: string[]) => {
  const sortedIds = [...shelterIds].sort();
  const key = sortedIds.join(",");

  return useQuery({
    queryKey: ["shelter-sections", key],
    enabled: shelterIds.length > 0,
    staleTime: 60_000,
    queryFn: async (): Promise<ShelterSectionsMap> => {
      if (sortedIds.length === 0) return {};

      const [needsRes, fundRes, adoptRes] = await Promise.all([
        supabase
          .from("shelter_urgent_needs")
          .select("id,shelter_id,text,sort_order,urgent_set_at")
          .in("shelter_id", sortedIds)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true }),
        supabase
          .from("shelter_fundraiser")
          .select("id,shelter_id,title,description,external_url,is_urgent,urgent_set_at,amount_raised,amount_goal,currency,stats_updated_at,stats_provider")
          .in("shelter_id", sortedIds),
        supabase
          .from("shelter_adoptions")
          .select(
            "id,shelter_id,name,age,species,description,photo_path,external_url,is_urgent,urgent_set_at,sort_order,created_at",
          )
          .in("shelter_id", sortedIds)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: false }),
      ]);

      if (needsRes.error) throw new Error(needsRes.error.message);
      if (fundRes.error) throw new Error(fundRes.error.message);
      if (adoptRes.error) throw new Error(adoptRes.error.message);

      const map: ShelterSectionsMap = {};
      const ensure = (id: string): ShelterSectionsBundle => {
        if (!map[id]) {
          map[id] = { urgentNeeds: [], fundraiser: null, adoption: null, hasAnySection: false };
        }
        return map[id];
      };

      for (const row of (needsRes.data ?? []) as ShelterUrgentNeed[]) {
        ensure(row.shelter_id).urgentNeeds.push(row);
      }
      // Jedna zbiorka per schronisko - pilne maja pierwszenstwo, potem dowolna.
      for (const row of (fundRes.data ?? []) as ShelterFundraiser[]) {
        const bundle = ensure(row.shelter_id);
        if (!bundle.fundraiser) {
          bundle.fundraiser = row;
          continue;
        }
        const currUrgent = isSectionUrgentActive(bundle.fundraiser.is_urgent, bundle.fundraiser.urgent_set_at);
        const newUrgent = isSectionUrgentActive(row.is_urgent, row.urgent_set_at);
        if (newUrgent && !currUrgent) bundle.fundraiser = row;
      }
      // Jedna adopcja per schronisko - pilne maja pierwszenstwo, potem najnowsza wg sort_order/created_at.
      const adoptionsByShelter = new Map<string, ShelterAdoption[]>();
      for (const row of (adoptRes.data ?? []) as (ShelterAdoption & { species: string })[]) {
        const norm: ShelterAdoption = {
          ...row,
          species: (row.species as AdoptionSpecies) ?? "inne",
        };
        const arr = adoptionsByShelter.get(norm.shelter_id) ?? [];
        arr.push(norm);
        adoptionsByShelter.set(norm.shelter_id, arr);
      }
      adoptionsByShelter.forEach((arr, sid) => {
        const urgent = arr.find((a) => isSectionUrgentActive(a.is_urgent, a.urgent_set_at));
        ensure(sid).adoption = urgent ?? arr[0] ?? null;
      });

      for (const id of Object.keys(map)) {
        const b = map[id];
        b.hasAnySection = b.urgentNeeds.length > 0 || !!b.fundraiser || !!b.adoption;
      }

      return map;
    },
  });
};

const EMPTY_BUNDLE: ShelterSectionsBundle = {
  urgentNeeds: [],
  fundraiser: null,
  adoption: null,
  hasAnySection: false,
};

export const getSectionsFor = (
  map: ShelterSectionsMap | undefined,
  shelterId: string,
): ShelterSectionsBundle => map?.[shelterId] ?? EMPTY_BUNDLE;

// Pelne sekcje dla widoku szczegolowego (modal / strona profilu).
// Rozni sie od useShelterSections tym, ze zwraca wszystkie urgent needs oraz do 3 adopcji
// (pilne pierwsze, potem wg sort_order/created_at). Jedna zbiorka per schronisko (pilna ma priorytet).
export interface ShelterFullSectionsBundle {
  urgentNeeds: ShelterUrgentNeed[];
  fundraiser: ShelterFundraiser | null;
  adoptions: ShelterAdoption[];
}

export const useShelterFullSections = (shelterId: string | undefined) => {
  return useQuery({
    queryKey: ["shelter-full-sections", shelterId],
    enabled: !!shelterId,
    staleTime: 60_000,
    queryFn: async (): Promise<ShelterFullSectionsBundle> => {
      if (!shelterId) return { urgentNeeds: [], fundraiser: null, adoptions: [] };

      const [needsRes, fundRes, adoptRes] = await Promise.all([
        supabase
          .from("shelter_urgent_needs")
          .select("id,shelter_id,text,sort_order,urgent_set_at")
          .eq("shelter_id", shelterId)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: true }),
        supabase
          .from("shelter_fundraiser")
          .select("id,shelter_id,title,description,external_url,is_urgent,urgent_set_at,amount_raised,amount_goal,currency,stats_updated_at,stats_provider")
          .eq("shelter_id", shelterId),
        supabase
          .from("shelter_adoptions")
          .select("id,shelter_id,name,age,species,description,photo_path,external_url,is_urgent,urgent_set_at,sort_order,created_at")
          .eq("shelter_id", shelterId)
          .order("sort_order", { ascending: true })
          .order("created_at", { ascending: false }),
      ]);

      if (needsRes.error) throw new Error(needsRes.error.message);
      if (fundRes.error) throw new Error(fundRes.error.message);
      if (adoptRes.error) throw new Error(adoptRes.error.message);

      const fundraisers = (fundRes.data ?? []) as ShelterFundraiser[];
      let fundraiser: ShelterFundraiser | null = null;
      for (const row of fundraisers) {
        if (!fundraiser) { fundraiser = row; continue; }
        const currUrgent = isSectionUrgentActive(fundraiser.is_urgent, fundraiser.urgent_set_at);
        const newUrgent = isSectionUrgentActive(row.is_urgent, row.urgent_set_at);
        if (newUrgent && !currUrgent) fundraiser = row;
      }

      const allAdoptions = ((adoptRes.data ?? []) as (ShelterAdoption & { species: string })[]).map(
        (row): ShelterAdoption => ({ ...row, species: (row.species as AdoptionSpecies) ?? "inne" }),
      );
      // Pilne adopcje pierwsze, zachowujac wewnetrzna kolejnosc (sort_order/created_at).
      const urgentAdoptions = allAdoptions.filter((a) => isSectionUrgentActive(a.is_urgent, a.urgent_set_at));
      const restAdoptions = allAdoptions.filter((a) => !isSectionUrgentActive(a.is_urgent, a.urgent_set_at));
      const adoptions = [...urgentAdoptions, ...restAdoptions].slice(0, 3);

      return {
        urgentNeeds: (needsRes.data ?? []) as ShelterUrgentNeed[],
        fundraiser,
        adoptions,
      };
    },
  });
};

